"use client";

import React from "react";
import { useReveal } from "./useReveal";
import css from "./fade-in.module.scss";

/**
 * The tags this is used on. They differ only in semantics — every one of them
 * takes the same attributes and the same ref for our purposes, which is what
 * lets the single cast below stand in for a fully polymorphic component.
 */
type FadeInTag = "div" | "section" | "li" | "figure";

interface FadeInProps extends React.ComponentPropsWithoutRef<"div">
{
    /** Tag to render. Defaults to a div; the project grid needs `li`. */
    as?: FadeInTag;
}

/**
 * Fades its children in over half a second, with a 20px rise, the first time
 * they scroll into view — and leaves them alone from then on.
 *
 * The observer holds off until the element is 12% inside the viewport, so the
 * fade plays where the reader is looking instead of finishing off-screen.
 */
export const FadeIn = ({ as = "div", className, ...props }: FadeInProps): React.ReactElement =>
{
    const { ref, shown } = useReveal<HTMLDivElement>({once: true, rootMargin: "0px 0px -12% 0px"});

    // Every tag in FadeInTag carries HTMLDivElement's attributes and accepts its
    // ref; naming one of them keeps TypeScript from having to prove that.
    const Tag = as as "div";

    return (
        <Tag
            {...props}
            ref={ref}
            className={`${css["fadeIn"]}${shown ? ` ${css["shown"]}` : ""}${className !== undefined ? ` ${className}` : ""}`}
        />
    );
};
